import Header from "./Header";
import Sidebar from "./Sidebar";
import TabSwitch from "./TabSwitch";
import React, { useState, useEffect } from "react";
import { Order, TabType } from "./types";
import { ChevronLeft } from "lucide-react";
import { FileQuestion } from "lucide-react";
import PrepareOrder from "../PrepareOrder/PrepareOrder";
import LabOrderDetails from "./OrderDetails/LabOrderDetails";
import { OrderHistory } from "../../../../Orders/OrderHistory";
import MedicineOrderDetails from "./OrderDetails/MedicineOrderDetails";
import GeneratePrescriptionForm, {
  PrescriptionFormData,
} from "./OrderDetails/GeneratePrescriptionForm";
import WithoutPrescriptionMedicineOrderDetails from "./OrderDetails/WithoutPrescriptionMedicineOrderDetails";
import { AdminOrderService } from "../../../../../services/api/AdminOrderService/admin.order.service";

const MedicalOrdersApp = () => {
  const [medicineOrders, setMedicineOrders] = useState<Order[]>([]);
  const [labOrders, setLabOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPrepareOrder, setShowPrepareOrder] = useState(false);
  const [showOrderHistory, setShowOrderHistory] = useState(false);
  const [
    showWithoutPrescriptionOrderDetails,
    setShowWithoutPrescriptionOrderDetails,
  ] = useState(false);
  const [showGeneratePrescriptionForm, setShowGeneratePrescriptionForm] =
    useState(false);
  const [prescriptionImageData, setPrescriptionImageData] = useState<{
    image: string | null;
    formData: PrescriptionFormData | null;
  }>({ image: null, formData: null });

  const [withPrescription, setWithPrescription] = useState(true);
  const [orderStatus, setOrderStatus] = useState<
    "COMPLETED" | "PENDING_PATIENT" | "DELIVERY_ASSIGNED" | undefined
  >();
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    return (localStorage.getItem("activeTab") as TabType) || "medicine";
  });
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const filteredMedicineCount = React.useMemo(() => {
    return medicineOrders.filter((order) =>
      withPrescription ? order.hasPrescription : !order.hasPrescription
    ).length;
  }, [medicineOrders, withPrescription]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSelectedOrder(null);
    localStorage.setItem("activeTab", tab);
  };

  const handlePrescriptionChange = (newWithPrescription: boolean) => {
    setWithPrescription(newWithPrescription);
    setShowWithoutPrescriptionOrderDetails(!newWithPrescription);
    setSelectedOrder(null);
    setPrescriptionImageData({ image: null, formData: null });
    setCurrentPage(1);
    setOrderStatus(undefined);
  };

  const handleOrderStatusChange = (newOrderStatus: "awaiting" | "approved") => {
    setOrderStatus(newOrderStatus);
    // Clear selected order when switching order status for lab orders
    let apiOrderStatus: "PENDING_PATIENT" | "DELIVERY_ASSIGNED" | undefined;

    if (newOrderStatus === "awaiting") {
      apiOrderStatus = "PENDING_PATIENT";
    } else if (newOrderStatus === "approved") {
      apiOrderStatus = "DELIVERY_ASSIGNED";
    } else {
      apiOrderStatus = undefined;
    }

    setOrderStatus(apiOrderStatus);
    setSelectedOrder(null);
    setCurrentPage(1);
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await AdminOrderService.getAdminOrdersListing(
        currentPage,
        pageSize,
        {
          hasPrescription: withPrescription,
          orderStatus: orderStatus,
        }
      );

      console.log("Raw API Response:", response); 

      const orders = response?.data?.data || [];
      const totalItemsCount = response?.data?.pagination?.totalCount || 0;
      const totalPagesCount = response?.data?.pagination?.totalPages || 1;

      setTotalCount(totalItemsCount);
      setTotalPages(totalPagesCount);

      console.log("Total Items Count:", totalItemsCount);
      console.log("Total Page Count", totalPagesCount);
      console.log("Raw Orders Data:", orders); // Debug raw orders

      const transformedOrders = orders.map((order: any) => {
        const labStatus =
          order.orderStatus === "PENDING_ADMIN"
            ? "new"
            : order.orderStatus === "AWAITING_RESULTS"
            ? "awaiting"
            : "approved";

        // Better patient name handling
        const patientName =
          order.patientName ||
          order.patient?.name ||
          `Patient ${order.patientId?.slice(0, 6) || "XXXXXX"}`;

        return {
          ...order,
          id: order.id,
          patientName,
          orderNumber: order.orderNumber || `ORD-${order.id}`,
          orderType: order.orderType || "UNKNOWN",
          date: new Date(order.orderDate).toLocaleDateString(),
          details: (order.productOrderItems || [])
            .map((item: any) => item.productName)
            .join(", "),
          labStatus,
          hasPrescription: order.hasPrescription === true, // Explicit boolean conversion
          paymentStatus: order.paymentStatus || "Pending",
          status: (order.paymentStatus || "Pending").toLowerCase(),
        };
      });

      console.log("Transformed Orders:", transformedOrders); // Debug transformed data

      const medOrders = transformedOrders.filter((order) =>
        order.orderType?.toUpperCase().includes("PRESCRIPTION")
      );

      console.log("Medicine Orders:", medOrders);
      console.log("Lab Orders:", labOrders);

        setMedicineOrders(medOrders);
        setLabOrders(labOrders);
    } catch (err) {
      console.error("Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
  fetchOrders();
}, [currentPage, withPrescription, orderStatus, activeTab]);


  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleOrderCancelled = (orderId: string) => {
    setMedicineOrders((prevOrders) =>
      prevOrders.map((order) =>
        order.id === orderId ? { ...order, orderStatus: "CANCELLED" } : order
      )
    );
    fetchOrders();
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50 ">
        {showPrepareOrder ? (
          <PrepareOrder
            onExit={() => setShowPrepareOrder(false)}
            prescriptionImageData={prescriptionImageData?.image || null}
            prescriptionFormData={prescriptionImageData?.formData || null}
            selectedOrder={selectedOrder}
            fetchOrder={fetchOrders}
          />
        ) : showOrderHistory ? (
          <div>
            <button
              className="text-sm mb-4 flex"
              onClick={() => setShowOrderHistory(false)}
            >
              <div className="flex">
                <ChevronLeft className="w-5 h-5 mr-2 cursor-pointer bg-gray-200 rounded-full" />
                <span className="text-md">Back to Orders</span>
              </div>
            </button>
            <OrderHistory />
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center w-full bg-white border border-gray-200 rounded-lg p-1">
              <div>
                <TabSwitch
                  activeTab={activeTab}
                  onTabChange={handleTabChange}
                  medicineCount={filteredMedicineCount}
                  labCount={labOrders?.length || 0}
                />
              </div>

              <div>
                <Header
                  activeTab={activeTab}
                  setShowOrderHistory={setShowOrderHistory}
                  setShowWithoutPrescriptionOrderDetails={
                    setShowWithoutPrescriptionOrderDetails
                  }
                  withPrescription={withPrescription}
                  setWithPrescription={handlePrescriptionChange}
                  orderStatus={orderStatus}
                  setOrderStatus={handleOrderStatusChange}
                />
              </div>
            </div>
            <div className="flex w-full">
              <Sidebar
                activeTab={activeTab}
                onTabChange={(tab) => {
                  setActiveTab(tab);
                  setCurrentPage(1);
                }}
                medicineOrders={medicineOrders}
                labOrders={labOrders}
                onOrderSelect={(order) =>
                  setSelectedOrder((prevOrder) =>
                    prevOrder?.id === order.id ? null : order
                  )
                }
                withPrescription={withPrescription}
                orderStatus={orderStatus}
                selectedOrder={selectedOrder}
                currentPage={currentPage}
                totalCount={totalCount}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />

              <div className="flex-1">
                {activeTab === "medicine" &&
                  (selectedOrder ? (
                    selectedOrder.hasPrescription ? (
                      <MedicineOrderDetails
                        onPrepareOrder={() => setShowPrepareOrder(true)}
                        showWithoutPrescriptionOrderDetails={
                          showWithoutPrescriptionOrderDetails
                        }
                        setShowGeneratePrescriptionForm={
                          setShowGeneratePrescriptionForm
                        }
                        selectedOrder={selectedOrder}
                        onOrderCancelled={handleOrderCancelled}
                        fetchOrder={fetchOrders}
                      />
                    ) : (
                      <WithoutPrescriptionMedicineOrderDetails
                        setShowGeneratePrescriptionForm={
                          setShowGeneratePrescriptionForm
                        }
                        selectedOrder={selectedOrder}
                      />
                    )
                  ) : (
                    <div className="p-6 border border-dashed border-cyan-300 rounded-lg text-center text-gray-500 flex flex-col items-center gap-2 w-full min-h-screen">
                      <FileQuestion className="h-10 w-10 text-gray-400" />
                      <p className="text-lg font-medium">No order selected</p>
                      <p className="text-sm text-gray-400">
                        Please select a medicine order from the list to view its
                        details.
                      </p>
                    </div>
                  ))}

                {activeTab === "lab" &&
                  (selectedOrder ? (
                    <LabOrderDetails orderStatus={orderStatus} />
                  ) : (
                    <div className="p-6 border border-dashed border-cyan-300 rounded-lg text-center text-gray-500 flex flex-col items-center gap-2 w-full min-h-screen">
                      <FileQuestion className="h-10 w-10 text-gray-400" />
                      <p className="text-lg font-medium">No order selected</p>
                      <p className="text-sm text-gray-400">
                        Please select a lab order from the list to view its
                        details.
                      </p>
                    </div>
                  ))}
              </div>
            </div>
          </>
        )}

        {showGeneratePrescriptionForm && (
          <GeneratePrescriptionForm
            setShowGeneratePrescriptionForm={setShowGeneratePrescriptionForm}
            setPrescriptionImageData={setPrescriptionImageData}
            onSavePrescription={() => setShowPrepareOrder(true)}
            selectedOrder={selectedOrder}
            patientId={selectedOrder?.patientId}
          />
        )}
      </div>
    </>
  );
};

export default MedicalOrdersApp;
